import { isNative } from '@/platform/platform';

/**
 * Haptics. On a phone, the platform's own engine through Capacitor; in a
 * browser, `navigator.vibrate`, which Android Chrome honors and iOS Safari
 * ignores. Call sites do not know which they got: the function is fire and
 * forget, and a device with no motor is silent rather than an error.
 */
export type HapticKind = 'light' | 'medium' | 'heavy' | 'success' | 'double';

const PATTERNS: Record<HapticKind, number | number[]> = {
  light: 10,
  medium: 20,
  heavy: 35,
  success: [12, 40, 18],
  double: [18, 120, 18],
};

export function haptic(kind: HapticKind, enabled = true): void {
  if (!enabled) return;
  if (isNative()) {
    void nativeHaptic(kind);
    return;
  }
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(PATTERNS[kind]);
    }
  } catch {
    // Some browsers throw without a user gesture; ignore.
  }
}

async function nativeHaptic(kind: HapticKind): Promise<void> {
  try {
    const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics');
    switch (kind) {
      case 'light':
        await Haptics.impact({ style: ImpactStyle.Light });
        return;
      case 'medium':
        await Haptics.impact({ style: ImpactStyle.Medium });
        return;
      case 'heavy':
        await Haptics.impact({ style: ImpactStyle.Heavy });
        return;
      case 'success':
        await Haptics.notification({ type: NotificationType.Success });
        return;
      case 'double':
        await Haptics.impact({ style: ImpactStyle.Medium });
        await new Promise((resolve) => setTimeout(resolve, 120));
        await Haptics.impact({ style: ImpactStyle.Medium });
        return;
      default: {
        const never: never = kind;
        throw new Error(String(never));
      }
    }
  } catch {
    // No motor, or the plugin is absent from this build: nothing to feel.
  }
}
